import { modulePermission, PERMISSIONS, PERSONAL_MODULES, type RolePermission, type Scope } from './permissions';
import type { QueueFunction } from './process';

/** Początkowe role — macierz z docs/06-uprawnienia.md. Po utworzeniu edytowalne w bazie. */
export interface RoleDef {
  code: string;
  name: string;
  /** kolejki zadań w sprawach, które niesie rola (osoba z rolą dostaje te zadania w branżach z karty) */
  queues: QueueFunction[];
  permissions: RolePermission[];
}

const p = (spec: Record<string, Scope | Scope[]>): RolePermission[] =>
  Object.entries(spec).flatMap(([permission, s]) =>
    (Array.isArray(s) ? s : [s]).map((scope) => ({ permission, scope })),
  );

/** Moduły lewego menu widoczne dla roli (uzgodnione z użytkownikiem 29.10.2026, docs/06 §5). */
const modules = (...codes: string[]): Record<string, Scope> => Object.fromEntries(codes.map((c) => [modulePermission(c), 'W' as Scope]));

/**
 * Minimum do pracy w kolejce (dawniej „uprawnienia funkcji”, do 02.11.2026): to, czego wymagają zadania kolejki
 * i ich wykonanie, plus moduły do tych zadań. Rola z kolejką zawsze je zawiera (scalane w DEFAULT_ROLES).
 */
export const QUEUE_MINIMUM: Record<QueueFunction, readonly RolePermission[]> = {
  opiekun: p({
    ...modules('moje_zadania', 'moje_sprawy'),
    'sprawy.podglad': 'W', 'klienci.podglad': 'W', 'sprawy.przejmowanie': 'W', 'sprawy.nadanie_biegu': 'O', 'sprawy.edycja': 'O', 'sprawy.wstrzymanie': 'O', 'sprawy.zamkniecie': 'O', 'protokoly.wysylka': 'O',
  }),
  handlowiec: p({
    ...modules('moje_zadania', 'moje_sprawy'),
    'sprawy.podglad': 'W', 'klienci.podglad': 'W', 'oferty.podglad': 'W', 'oferty.edycja': 'O', 'oferty.wydanie': 'O', 'oferty.udostepnianie': 'O', 'oferty.akceptacja_reczna': 'O', 'katalog.podglad': 'W', 'ceny.sprzedaz': 'W',
  }),
  specjalista_umow: p({
    ...modules('moje_zadania', 'moje_sprawy'),
    'sprawy.podglad': 'W', 'klienci.podglad': 'W', 'oferty.podglad': 'W', 'ceny.sprzedaz': 'W', 'umowy.podglad': 'W', 'umowy.edycja': 'W', 'umowy.wydanie': 'W', 'umowy.podpis': 'W',
  }),
  koordynator: p({
    ...modules('moje_zadania', 'moje_sprawy', 'planowanie'),
    'sprawy.podglad': 'W', 'klienci.podglad': 'W', 'planowanie.edycja': 'W', 'kalendarz.podglad': 'W', 'zadania.podglad': 'W',
  }),
  ksiegowy: p({
    ...modules('moje_zadania', 'moje_sprawy', 'finanse'),
    'sprawy.podglad': 'W', 'klienci.podglad': 'W', 'umowy.podglad': 'W', 'ceny.sprzedaz': 'W', 'finanse.podglad': 'W', 'finanse.wplaty': 'W', 'faktury.podglad': 'W', 'faktury.wystawianie': 'W',
  }),
  magazynier: p({
    ...modules('magazyn'),
    'katalog.podglad': 'W', 'magazyn.podglad': 'W', 'magazyn.ruchy': 'W', 'magazyn.rezerwacje': 'W', 'magazyn.kompletacja': 'W', 'zapotrzebowania.podglad': 'W', 'zapotrzebowania.zglaszanie': 'W',
  }),
};

/** Moduły „moje” (Moje zadania, Mój kalendarz, Moje sprawy) — domyślne uprawnienia każdej roli, także nowej. */
export const PERSONAL_PERMISSIONS: readonly RolePermission[] = PERSONAL_MODULES.map((c) => ({ permission: modulePermission(c), scope: 'W' as Scope }));

/** Rola: uprawnienia własne + minimum jej kolejek (bez powtórzeń). */
const role = (code: string, name: string, queues: QueueFunction[], permissions: RolePermission[]): RoleDef => {
  const seen = new Set<string>();
  // moduły „moje” — domyślnie w każdej roli (do odebrania w edytorze ról)
  const all = [...permissions, ...PERSONAL_PERMISSIONS, ...queues.flatMap((q) => QUEUE_MINIMUM[q])].filter((x) => {
    const k = `${x.permission}:${x.scope}`;
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
  return { code, name, queues, permissions: all };
};

const FIELD_COMMON: Record<string, Scope | Scope[]> = {
  'klienci.podglad': 'P',
  'instalacje.podglad': 'P',
  'urzadzenia.dodawanie': 'P',
  'katalog.podglad': 'P',
  'kalendarz.podglad': ['E', 'O'],
  'nieobecnosci.wniosek': 'O',
  'zadania.podglad': 'P',
  'zadania.status': 'P',
  'zadania.dokumentacja': 'P',
  'zadania.materialy': 'P',
  'zadania.blokada': 'P',
  'zapotrzebowania.zglaszanie': 'P',
  'protokoly.podpis': 'P',
  'serwis.podglad': 'P',
  'sprawy.tworzenie': 'O',
};

/** uprawnienia, których właściciel nie dostaje z automatu (może je nadać sobie prawami indywidualnymi) */
export const OWNER_EXCLUDED: readonly string[] = ['flota.przypomnienia'];

const RAW_ROLES: RoleDef[] = [
  {
    code: 'wlasciciel',
    name: 'Właściciel',
    // właściciel może dostawać zadania każdej kolejki (w branżach z karty); domyślnie wszystkie — jak dotąd funkcje właściciela
    queues: ['opiekun', 'handlowiec', 'specjalista_umow', 'koordynator', 'ksiegowy'],
    // przypomnienia floty idą do zarządzającego flotą, nie do każdego z pełnym dostępem (docs/19 D3)
    permissions: PERMISSIONS.filter((perm) => !OWNER_EXCLUDED.includes(perm.code)).map((perm) => ({ permission: perm.code, scope: 'W' as Scope })),
  },
  {
    code: 'handlowiec',
    name: 'Handlowiec',
    queues: ['handlowiec'],
    permissions: p({
      ...modules('moje_zadania', 'moje_sprawy', 'sprawy', 'klienci', 'katalog', 'kalendarz'),
      'klienci.podglad': 'W',
      'klienci.edycja': 'W',
      'klienci.korespondencja': 'W',
      'instalacje.podglad': 'W',
      'sprawy.podglad': 'W',
      'sprawy.tworzenie': 'W',
      'sprawy.edycja': 'O',
      'sprawy.nadanie_biegu': 'O',
      'sprawy.wstrzymanie': 'O',
      'sprawy.zamkniecie': 'O',
      'zakresy.zmiana_firmy': 'O',
      'zakresy.przegrana': 'O',
      'dokumenty.dodawanie': 'O',
      'oferty.podglad': 'W',
      'oferty.edycja': 'O',
      'oferty.wydanie': 'O',
      'oferty.udostepnianie': 'O',
      'oferty.wycofanie': 'O',
      'oferty.akceptacja_reczna': 'O',
      'katalog.podglad': 'W',
      'ceny.sprzedaz': 'W',
      'ceny.marza': 'O',
      // umowy przygotowuje i prowadzi tylko specjalista ds. umów — handlowiec je widzi
      'umowy.podglad': 'W',
      'finanse.podglad': 'O',
      'faktury.podglad': 'O',
      'warunki.podglad': 'W',
      'kalendarz.podglad': 'W',
      'planowanie.edycja': 'O',
      'nieobecnosci.wniosek': 'O',
      'zadania.podglad': 'O',
      'zadania.dokumentacja': 'O',
      'zadania.blokada': 'O',
      'protokoly.wysylka': 'O',
      'serwis.podglad': 'W',
      'serwis.tworzenie': 'W',
      'audyt.podglad': 'O',
      'zapotrzebowania.podglad': 'O',
    }),
  },
  {
    // przejmuje nowe sprawy, weryfikuje, nadaje bieg, nadzoruje i zamyka (docs/10) — do łączenia z rolą handlowca
    code: 'opiekun',
    name: 'Opiekun sprawy',
    queues: ['opiekun'],
    permissions: p({
      ...modules('moje_zadania', 'moje_sprawy', 'sprawy', 'klienci', 'katalog', 'planowanie', 'kalendarz', 'ekipy', 'serwis', 'instalacje'),
      'klienci.podglad': 'W',
      'klienci.edycja': 'W',
      'klienci.korespondencja': 'W',
      'klienci.scalanie': 'W',
      'instalacje.podglad': 'W',
      'katalog.podglad': 'W',
      'planowanie.edycja': 'O',
      'sprawy.podglad': 'W',
      'sprawy.tworzenie': 'W',
      'sprawy.edycja': 'O',
      'sprawy.przejmowanie': 'W',
      'sprawy.nadanie_biegu': 'O',
      'sprawy.wstrzymanie': 'O',
      'sprawy.zamkniecie': 'O',
      'sprawy.wznowienie': 'O',
      'obieg.przydzielanie': 'O',
      'dokumenty.dodawanie': 'O',
      'oferty.podglad': 'W',
      'umowy.podglad': 'W',
      'ceny.sprzedaz': 'W',
      'finanse.podglad': 'O',
      'faktury.podglad': 'O',
      'warunki.podglad': 'W',
      'kalendarz.podglad': 'W',
      'zadania.podglad': 'O',
      'serwis.podglad': 'W',
      'audyt.podglad': 'O',
    }),
  },
  {
    // przygotowanie umów z zaakceptowanych ofert, wysyłka i podpis — do łączenia z rolą handlowca
    code: 'specjalista_umow',
    name: 'Specjalista ds. umów',
    queues: ['specjalista_umow'],
    permissions: p({
      ...modules('moje_zadania', 'moje_sprawy', 'klienci', 'katalog'),
      'klienci.podglad': 'W',
      'klienci.edycja': 'W',
      'instalacje.podglad': 'W',
      'katalog.podglad': 'W',
      'sprawy.podglad': 'W',
      'dokumenty.dodawanie': 'W',
      'oferty.podglad': 'W',
      'ceny.sprzedaz': 'W',
      'umowy.podglad': 'W',
      'umowy.edycja': 'W',
      'umowy.wydanie': 'W',
      'umowy.podpis': 'W',
      'umowy.podpis_elektroniczny': 'W',
      'umowy.aneks': 'W',
      'finanse.podglad': 'W',
      'warunki.podglad': 'W',
    }),
  },
  {
    code: 'koordynator',
    name: 'Koordynator',
    queues: ['koordynator'],
    permissions: p({
      ...modules('moje_zadania', 'moje_sprawy', 'planowanie', 'kalendarz', 'ekipy', 'pracownicy'),
      'pracownicy.podglad': 'W',
      'klienci.podglad': 'W',
      'klienci.edycja': 'W',
      'klienci.scalanie': 'W',
      'instalacje.podglad': 'W',
      'urzadzenia.dodawanie': 'W',
      'urzadzenia.edycja': 'W',
      'sprawy.podglad': 'W',
      'sprawy.tworzenie': 'W',
      'sprawy.edycja': 'W',
      'sprawy.wstrzymanie': 'W',
      'dokumenty.dodawanie': 'W',
      'katalog.podglad': 'W',
      'warunki.podglad': 'W',
      'kalendarz.podglad': 'W',
      'planowanie.edycja': 'W',
      'planowanie.konflikt': 'W',
      'ekipy.zarzadzanie': 'W',
      'nieobecnosci.wniosek': 'O',
      'nieobecnosci.zatwierdzanie': 'W',
      'kalendarz.wpisy': 'W',
      'zadania.podglad': 'W',
      'zadania.status': 'W',
      'zadania.dokumentacja': 'W',
      'zadania.materialy': 'W',
      'zadania.blokada': 'W',
      'blokady.rozwiazanie': 'W',
      'protokoly.podpis': 'W',
      'protokoly.uniewaznienie': 'W',
      'protokoly.wysylka': 'W',
      'serwis.podglad': 'W',
      'serwis.tworzenie': 'W',
      'przeglady.edycja': 'W',
      'urzadzenia_mobilne.uniewaznienie': 'W',
      'procesy.edycja': 'W',
      'audyt.podglad': 'O',
      'flota.dostepnosc': 'W',
      // gotowość materiałowa przy planowaniu (bez wartości — te wymagają ceny.zakup)
      'magazyn.podglad': 'W',
      ...modules('magazyn'),
      'zapotrzebowania.podglad': 'W',
      'zapotrzebowania.zglaszanie': 'W',
    }),
  },
  {
    // stany, ruchy i dokumenty magazynów (dane firmowe — firmy pracownika; docs/14); magazyny można zawęzić na karcie pracownika
    code: 'magazynier',
    name: 'Magazynier',
    queues: ['magazynier'],
    permissions: p({
      ...modules('moje_zadania', 'magazyn', 'katalog', 'finanse'),
      'katalog.podglad': 'W',
      'ceny.zakup': 'W',
      'magazyn.podglad': 'W',
      'magazyn.ruchy': 'W',
      'magazyn.korekty': 'W',
      'magazyn.rezerwacje': 'W',
      'magazyn.kompletacja': 'W',
      'zakupy.zamowienia': 'W',
      'zakupy.zlozenie': 'W',
      'dostawcy.edycja': 'W',
      'zakupy.podglad': 'W',
      'zapotrzebowania.podglad': 'W',
      'zapotrzebowania.zglaszanie': 'W',
    }),
  },
  {
    // obieg sprawy: faktury zaliczkowe / zamówienia po podpisie umowy; końcowa — po odbiorze (D obieg spraw)
    code: 'ksiegowosc',
    name: 'Księgowość',
    queues: ['ksiegowy'],
    permissions: p({
      // „klienci” — dane nabywcy do faktury (podgląd kartoteki; QA 31.10: P2)
      ...modules('moje_zadania', 'moje_sprawy', 'klienci', 'finanse', 'zestawienia', 'firmy'),
      'zestawienia.podglad': 'W',
      'klienci.podglad': 'W',
      'sprawy.podglad': 'W',
      'umowy.podglad': 'W',
      'ceny.sprzedaz': 'W',
      'finanse.podglad': 'W',
      'finanse.wplaty': 'W',
      'faktury.podglad': 'W',
      'faktury.wystawianie': 'W',
      'faktury.korekta': 'W',
      'zakupy.podglad': 'W',
      'zakupy.obsluga': 'W',
      'zakupy.zamowienia': 'W',
      'warunki.podglad': 'W',
    }),
  },
  {
    code: 'instalator',
    name: 'Instalator',
    queues: [],
    permissions: p(FIELD_COMMON),
  },
  {
    // praca w terenie (aplikacja mobilna) i planowanie serwisu i przeglądów w panelu (29.10.2026: scalona z rolą „Serwis”)
    code: 'serwisant',
    name: 'Serwisant',
    queues: [],
    permissions: [
      ...p({ ...FIELD_COMMON, 'urzadzenia.edycja': 'P', 'serwis.tworzenie': 'P' }),
      ...p({
        ...modules('moje_zadania', 'moje_sprawy', 'klienci', 'serwis', 'instalacje', 'planowanie'),
        'klienci.podglad': 'W',
        'instalacje.podglad': 'W',
        'urzadzenia.dodawanie': 'W',
        'urzadzenia.edycja': 'W',
        'sprawy.podglad': 'W',
        'serwis.podglad': 'W',
        'serwis.tworzenie': 'W',
        'przeglady.edycja': 'W',
        // docs/26: pozycje do zapłaty z cennika (i spoza cennika), płatność klienta na miejscu
        'serwis.platnosci': 'W',
        'planowanie.edycja': 'W',
        'kalendarz.podglad': 'W',
        'zadania.podglad': 'W',
        'protokoly.wysylka': 'W',
      }),
    ],
  },
  {
    // moduł Flota (docs/19): dane firmowe — przy wybranych firmach pracownika tylko pojazdy tych firm
    code: 'zarzadzajacy_flota',
    name: 'Zarządzający flotą',
    queues: [],
    permissions: p({
      ...modules('moje_zadania', 'flota', 'kalendarz'),
      'flota.podglad': 'W',
      'flota.zarzadzanie': 'W',
      'flota.koszty': 'W',
      'flota.import': 'W',
      'flota.przypomnienia': 'W',
      'flota.dostepnosc': 'W',
      'kalendarz.podglad': 'W',
      'nieobecnosci.wniosek': 'O',
    }),
  },
];

/** Role startowe: uprawnienia własne + minimum kolejek. */
export const DEFAULT_ROLES: readonly RoleDef[] = RAW_ROLES.map((r) => role(r.code, r.name, r.queues, r.permissions));
